import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { InternalFieldName } from 'react-hook-form/dist/types/fields';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Form from '../../components/Crud/Form';

export interface ProfileFields {
  name: string;
  email: string;
  oldPassword?: string;
  newPassword?: string;
  passwordConfirm?: string;
}

interface ProfileProps {
  isLoading: boolean;
  title: string;
  formFields: ProfileFields;
  formErrors: Record<string, any>;
  onSubmit: (data: any) => void;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function ProfileForm({
  isLoading,
  title,
  formFields,
  formErrors,
  onSubmit,
}: ProfileProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
    getValues,
  } = useForm<ProfileFields>();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  useEffect(() => {
    reset(formFields);
  }, [formFields]);

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  return (
    <Form isLoading={isLoading} title={title} onSubmitCallback={() => {}}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='mb-3 row'>
          <div className='col-md-6'>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor='inputLabel' className='form-label'>
              Nome
            </label>
            <input
              {...register('name', { required: true })}
              className='form-control'
              placeholder='Nome'
              type='text'
              required
            />
            {errors.name && (
              <div className='form-text text-danger'>{errors.name.message}</div>
            )}
          </div>
          <div className='col-md-6'>
            <div className='mb-3'>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor='inputLabel' className='form-label'>
                E-mail
              </label>
              <input
                {...register('email', { required: true })}
                className='form-control'
                placeholder='E-mail'
                type='email'
                required
              />
              {errors.email && (
                <div className='form-text text-danger'>
                  {errors.email.message}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='col-md-4 mb-3'>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor='inputLabel' className='form-label'>
              Senha atual
            </label>
            <div className='input-group flex-nowrap'>
              <span
                className='input-group-text'
                onClick={() => setShowOldPassword(!showOldPassword)}
                role='button'
                aria-hidden='true'
              >
                {showOldPassword && <FaEyeSlash />}
                {!showOldPassword && <FaEye />}
              </span>
              <input
                {...register('oldPassword', {
                  required: false,
                })}
                className='form-control'
                placeholder='Senha atual'
                autoComplete='new-password'
                type={showOldPassword ? 'text' : 'password'}
              />
            </div>
            <div className='form-text'>
              Deixe em branco para manter a senha atual
            </div>
            {errors.oldPassword && (
              <div className='form-text text-danger'>
                {errors.oldPassword.message}
              </div>
            )}
          </div>
          <div className='col-md-4 mb-3'>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor='inputLabel' className='form-label'>
              Nova senha
            </label>
            <div className='input-group flex-nowrap'>
              <span
                className='input-group-text'
                onClick={() => setShowNewPassword(!showNewPassword)}
                role='button'
                aria-hidden='true'
              >
                {showNewPassword && <FaEyeSlash />}
                {!showNewPassword && <FaEye />}
              </span>
              <input
                {...register('newPassword', {
                  required: false,
                  minLength: {
                    value: 8,
                    message:
                      'Este valor é muito curto. Deve ter 8 caracteres ou mais.',
                  },
                })}
                className='form-control'
                placeholder='Nova senha'
                type={showNewPassword ? 'text' : 'password'}
              />
            </div>
            {errors.newPassword && (
              <div className='form-text text-danger'>
                {errors.newPassword.message}
              </div>
            )}
          </div>
          <div className='col-md-4 mb-3'>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor='inputLabel' className='form-label'>
              Confirme a nova senha
            </label>
            <div className='input-group flex-nowrap'>
              <span
                className='input-group-text'
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                role='button'
                aria-hidden='true'
              >
                {showPasswordConfirm && <FaEyeSlash />}
                {!showPasswordConfirm && <FaEye />}
              </span>
              <input
                {...register('passwordConfirm', {
                  required: false,
                  validate: (value) => {
                    const { newPassword } = getValues();
                    return newPassword === value || 'As senhas não conferem';
                  },
                  minLength: {
                    value: 8,
                    message:
                      'Este valor é muito curto. Deve ter 8 caracteres ou mais.',
                  },
                })}
                className='form-control'
                placeholder='Confirme a nova senha'
                type={showPasswordConfirm ? 'text' : 'password'}
              />
            </div>
            {errors.passwordConfirm && (
              <div className='form-text text-danger'>
                {errors.passwordConfirm.message}
              </div>
            )}
          </div>
        </div>

        <div className='d-flex justify-content-end gap-2'>
          <button type='submit' className='btn btn-primary'>
            Salvar
          </button>
        </div>
      </form>
    </Form>
  );
}
